import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminGetProducts, adminDeleteProduct, adminUpdateProduct } from '../../api';
import './AdminLayout.css';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    adminGetProducts({ search, limit: 100 })
      .then(r => { setProducts(r.data.products || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [search]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(product) {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    setDeleting(product._id);
    try {
      await adminDeleteProduct(product._id);
      setProducts(prev => prev.filter(p => p._id !== product._id));
    } finally {
      setDeleting(null);
    }
  }

  async function handleToggleActive(product) {
    try {
      const updated = await adminUpdateProduct(product._id, { isActive: !product.isActive });
      setProducts(prev => prev.map(p => p._id === product._id ? updated.data : p));
    } catch {}
  }

  const primaryImage = (p) => {
    const img = p.images?.find(i => i.isPrimary) || p.images?.[0];
    return img?.url || 'https://placehold.co/48x48/f5f5f5/aaa?text=?';
  };

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Products</h1>
          <p className="admin-page-sub">{products.length} total products</p>
        </div>
        <Link to="/admin/products/new" className="btn-primary">+ Add Product</Link>
      </div>

      {/* Search bar */}
      <div style={{ padding: '16px 28px' }}>
        <input
          type="text"
          placeholder="Search products…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 14px',
            fontSize: 14, width: '100%', maxWidth: 360, outline: 'none',
          }}
        />
      </div>

      <div style={{ padding: '0 28px 28px' }}>
        <div className="admin-card">
          {loading ? (
            <div className="admin-loading">Loading products…</div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <span>📦</span>
              <p>No products found.</p>
              <Link to="/admin/products/new" className="btn-primary">Add your first product</Link>
            </div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Images</th>
                    <th>Tags</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p._id}>
                      {/* Product cell */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <img
                            src={primaryImage(p)}
                            alt={p.name}
                            style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', background: '#f5f5f5', flexShrink: 0 }}
                          />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>{p.category?.name}</div>
                            {p.isBestseller && <span className="badge badge-yellow" style={{ fontSize: 10 }}>Bestseller</span>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 700 }}>₹{p.price?.toLocaleString('en-IN')}</div>
                        {p.originalPrice && (
                          <div style={{ fontSize: 12, color: '#9ca3af', textDecoration: 'line-through' }}>
                            ₹{p.originalPrice.toLocaleString('en-IN')}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${p.stock > 10 ? 'badge-green' : p.stock > 0 ? 'badge-yellow' : 'badge-red'}`}>
                          {p.stock}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {(p.images || []).slice(0, 3).map((img, i) => (
                            <img
                              key={i}
                              src={img.url}
                              alt={img.alt}
                              title={img.alt || 'No description'}
                              style={{ width: 32, height: 32, borderRadius: 5, objectFit: 'cover', border: img.isPrimary ? '2px solid #f59e0b' : '1px solid #e5e7eb' }}
                            />
                          ))}
                          {(p.images?.length || 0) > 3 && (
                            <div style={{ width: 32, height: 32, borderRadius: 5, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#6b7280' }}>
                              +{p.images.length - 3}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>
                        {p.tags?.slice(0,2).join(', ') || '—'}
                      </td>
                      <td>
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={p.isActive !== false}
                            onChange={() => handleToggleActive(p)}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn-icon"
                            title="Edit product"
                            onClick={() => navigate(`/admin/products/${p._id}`)}
                          >✏️</button>
                          <button
                            className="btn-icon"
                            title="Manage images"
                            onClick={() => navigate(`/admin/products/${p._id}/images`)}
                          >🖼️</button>
                          <button
                            className="btn-danger"
                            title="Delete product"
                            disabled={deleting === p._id}
                            onClick={() => handleDelete(p)}
                          >
                            {deleting === p._id ? '…' : '🗑️'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
